<template>
  <div class="badiu-tiptap-file-upload">
    <button
      type="button"
      class="badiu-tiptap-btn badiu-tiptap-btn-upload"
      :title="title"
      :disabled="uploading"
      @click="openSelector"
    >
      <i v-if="uploading" class="fas fa-spinner fa-spin"></i>
      <i v-else :class="['fas', icon]"></i>
    </button>

    <input
      ref="fileInputRef"
      type="file"
      class="badiu-tiptap-file-input"
      :accept="accept"
      @change="handleUpload"
    />

    <div v-if="errorMessage" class="badiu-tiptap-file-error">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
/**
 * BadiuTiptapFileUpload
 *
 * Generic upload component responsible for:
 *  - Opening a file selector dialog
 *  - Sending the selected file to the Badiu API
 *  - Emitting events with the public URL and file metadata
 *
 * Reused by:
 *  - BadiuTiptapImageUpload (images)
 *  - BadiuTiptapLink        (file attachments for links)
 *
 * URL structure (built from baseUrl + constants below):
 *  - Upload endpoint : {baseUrl}/system/file/set/single
 *  - Public read URL : {baseUrl}/system/file/get/{code}/{name}
 */
import { ref, computed } from 'vue'

// Path segments are fixed by the Badiu API contract and never change.
const FILE_SUBMIT_PATH = "/system/file/set/single";
const FILE_READ_PATH   = "/system/file/get/";

export default {
  name: "BadiuTiptapFileUpload",

  props: {
    accept: {
      type: String,
      default: "*/*"
    },

    filetype: {
      type: String,
      default: "file"
    },

    title: {
      type: String,
      default: "Upload file"
    },

    /** Font Awesome icon class (e.g. "fa-upload", "fa-paperclip"). */
    icon: {
      type: String,
      default: "fa-upload"
    },

    /** Objeto de configuração (ver BadiuTiptapEditor para doc das chaves). */
    config: {
      type: Object,
      default: () => ({})
    }
  },

  emits: ["uploaded", "error"],

  setup(props, { emit }) {
    const fileInputRef   = ref(null);
    const uploading      = ref(false);
    const errorMessage   = ref("");

    const DEFAULT_BASE_URL = "https://ds4.badiu21.com.br/badiunet/geral/public";

    const baseUrl = computed(() =>
      props.config?.upload_baseurl ?? DEFAULT_BASE_URL
    );

    /** axios vem de config.external_api_vendor.axios — sem fallback para window. */
    const axios = computed(() =>
      props.config?.external_api_vendor?.axios ?? null
    );

    /** Full endpoint used to submit files. */
    const uploadUrl = computed(() => baseUrl.value + FILE_SUBMIT_PATH);

    /** Base URL prefix used to build the public read URL after upload. */
    const publicReadUrl = computed(() => baseUrl.value + FILE_READ_PATH);

    function openSelector() {
      errorMessage.value = "";
      fileInputRef.value?.click();
    }

    async function handleUpload(event) {
      const file = event.target.files?.[0];
      event.target.value = "";

      if (!file) return;

      uploading.value    = true;
      errorMessage.value = "";

      try {
        if (!axios.value) {
          throw new Error("axios não disponível. Forneça external_api_vendor no config.");
        }

        const fd = new FormData();
        fd.append("file", file);
        fd.append("filetype", props.filetype);

        const response = await axios.value.post(uploadUrl.value, fd);

        if (
          response.data &&
          response.data.status === "accept" &&
          response.data.message
        ) {
          const fileCode  = response.data.message.code;
          const fileName  = response.data.message.name;
          const filepath  = fileCode + "/" + fileName;
          const publicUrl = publicReadUrl.value + filepath;

          emit("uploaded", {
            file,
            response: response.data,
            filepath,
            src: publicUrl,
            url: publicUrl,
            name: file.name
          });
        } else {
          throw new Error(
            (response.data && response.data.message) ||
              "Unexpected server response."
          );
        }
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          error.message ||
          "Failed to upload file.";

        errorMessage.value = msg;

        emit("error", { file, error, message: msg });
      } finally {
        uploading.value = false;
      }
    }

    return {
      fileInputRef,
      uploading,
      errorMessage,
      openSelector,
      handleUpload
    };
  }
};
</script>

<style>
.badiu-tiptap-file-upload {
  display: inline-block;
}

.badiu-tiptap-file-input {
  display: none !important;
}

.badiu-tiptap-btn-upload {
  background-color: #fff;
  color: #495057;
  border: 1px solid #ced4da;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  cursor: pointer;
}

.badiu-tiptap-btn-upload:hover {
  background-color: #e9ecef;
}

.badiu-tiptap-btn-upload:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.badiu-tiptap-file-error {
  margin-top: 0.25rem;
  font-size: 0.85rem;
  color: #dc3545;
}
</style>
